import React, { createContext, useContext, useState, useEffect } from 'react';

// Currency configuration with exchange rates (relative to USD)
export const currencies = {
  USD: { symbol: '$', name: 'US Dollar', rate: 1, code: 'USD' },
  EUR: { symbol: '€', name: 'Euro', rate: 0.92, code: 'EUR' },
  GBP: { symbol: '£', name: 'British Pound', rate: 0.79, code: 'GBP' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.53, code: 'AUD' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.36, code: 'CAD' },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 83.12, code: 'INR' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 1.34, code: 'SGD' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', rate: 1.64, code: 'NZD' }
};

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Try to get from localStorage
    const saved = localStorage.getItem('preferred_currency');
    return saved && currencies[saved] ? saved : 'USD';
  });

  useEffect(() => {
    localStorage.setItem('preferred_currency', currency);
  }, [currency]);

  // Convert USD amount to selected currency
  const convert = (amountInUSD) => {
    const rate = currencies[currency].rate;
    return amountInUSD * rate;
  };

  // Format amount with currency symbol
  const format = (amountInUSD, options = {}) => {
    const { decimals = 0, showCode = false } = options;
    const converted = convert(amountInUSD);
    const { symbol, code } = currencies[currency];
    
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    if (showCode) {
      return `${symbol}${formatted} ${code}`;
    }
    return `${symbol}${formatted}`;
  };

  // Format with K/M suffix for large numbers
  const formatCompact = (amountInUSD) => {
    const converted = convert(amountInUSD);
    const { symbol } = currencies[currency];
    
    if (converted >= 1000000) {
      return `${symbol}${(converted / 1000000).toFixed(1)}M`;
    }
    if (converted >= 1000) {
      return `${symbol}${(converted / 1000).toFixed(0)}K`;
    }
    return `${symbol}${converted.toFixed(0)}`;
  };

  const value = {
    currency,
    setCurrency,
    currencies,
    currentCurrency: currencies[currency],
    convert,
    format,
    formatCompact
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
